import { Divider, Input, Skeleton, Tooltip, Typography } from "antd";
import { InfoCircleOutlined, FileDoneOutlined } from "@ant-design/icons";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import "./paymentSummary.css";
import "../fontStyling.css";
import {
  selectCurrencyCode,
  selectFontFamily,
} from "../../../slices/PaymentSessionDetailsSlice";
import {
  selectActivePaymentMethod,
} from "../../../slices/PaymentButtonSlice";
import {
  blockInvalidChar,
  formatInputAmount,
  generateDynamicStyling,
  getNetAmount,
  getSanitizedAmount,
  isEmpty,
  numberToCurrency,
  surchargeDiscountedAmount,
} from "../../../Utils/utils";
import { ReactComponent as EDIT_ICON } from '../../../assets/edit_icon.svg'
import { selectIsMobileLayout } from "../../../slices/MobileLayoutSlice";
import {
  handleTotalPaymentSummaryError,
  handleTotalPaymentSummaryValue,
} from "../../../slices/HandleAdditionalComponentsSlice";
import TranslateText from "../../resuableComponents/TranslateText";
import { removeSelectedIntruments, selectSelectedIntrument } from "../../../slices/SavedInstrumentSlice";
import { setEmiPaymentDetails } from "../../../slices/EmiSlice";
import { setAppliedOffer, setAppliedOfferDetails } from "../../../slices/OfferSlice";
import { MAXIMUM_NUMBER_AMOUNT } from "../../../staticData";
import { setOnDemandAmount, setOnDemandLoaded } from "../../../slices/CommonPersistedSlice";
import { surchargeInformationEnteredEvent, onDemandAmountEditedEvent } from "../../../Utils/uiAnalytics";

const PaymentSummary = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [items, setItems] = useState(0);
  const sessionDetails = useSelector((state) => state.paymentSessionDetails.sessionDetails);
  const selectedInstrument = useSelector((state) => selectSelectedIntrument(state));
  const paymentDetails = useSelector((state) => state.paymentSessionDetails.sessionDetails.paymentDetails);
  const sessionDetailsLoading = useSelector((state) => state.paymentSessionDetails.sessionDetailsLoading);
  const showTotalPaymentSummaryField = useSelector((state) => state.additionalComponents.showTotalPaymentSummaryField);
  const totalPaymentSummaryValue = useSelector((state) => state.additionalComponents.totalPaymentSummaryValue);
  const showTotalPaymentSummaryError = useSelector((state) => state.additionalComponents.showTotalPaymentSummaryError);
  const sampleToken = useSelector((state) => state.paymentSessionDetails.sampleToken);
  const fontFamily = useSelector((state) => selectFontFamily(state));
  const isMobileLayout = useSelector((state) => selectIsMobileLayout(state));
  const currencyCode = useSelector((state) => selectCurrencyCode(state));
  const [paymentSummary, setPaymentSummary] = useState();
  const isDccQuotationSelected = useSelector((state) => state.cardDetails.isDccQuotationSelected);
  const dccQuotation = useSelector(state => state.cardDetails.dccQuotation);
  const dccMoney = dccQuotation?.dccQuotationDetails?.dccMoney?.amount;
  const issuerCurrencyDetails = useSelector(state => state.cardDetails.issuerCurrencyDetails);
  const activePaymentMethod = useSelector((state) => selectActivePaymentMethod(state));
  const activeSurchargeDetail = useSelector(state => state.surcharges.activeSurchargeDetail);
  const totalSurchargeAmount = useSelector(state => state.surcharges.totalSurchargeAmount);
  const surchargeDetail = useSelector(state => state.surcharges.surchargeDetail);
  const appliedOfferDetail = useSelector(state => state.offers.appliedOfferDetail);
  const uniqueCardNetworkArray = useSelector(state => state.paymentSessionDetails.uniqueCardNetworkArray);

  const uniqueAppliedSurcharges = useMemo(() => {
    const appliedSurcharges = surchargeDetail?.appliedSurcharges ?? [];
    const uniqueSurchargeMap = new Map();
    const hasValidDescription = (description) => {
      if (!description) return false;
      const plainDescription = typeof description === "string"
        ? description.replace(/<[^>]*>/g, "").trim()
        : String(description).trim();
      return plainDescription.length > 0;
    };

    appliedSurcharges.forEach((singleSurcharge) => {
      const title = singleSurcharge?.surchargeDetails?.title?.trim();
      const description = singleSurcharge?.surchargeDetails?.description;
      if (!title) return;

      const normalizedTitle = title.toLowerCase();
      if (!uniqueSurchargeMap.has(normalizedTitle)) {
        uniqueSurchargeMap.set(normalizedTitle, { title, description });
        return;
      }

      const existingSurcharge = uniqueSurchargeMap.get(normalizedTitle);
      const existingHasDescription = hasValidDescription(existingSurcharge?.description);
      const currentHasDescription = hasValidDescription(description);

      // For duplicate titles, prefer an item that has a description.
      if (!existingHasDescription && currentHasDescription) {
        uniqueSurchargeMap.set(normalizedTitle, { title, description });
      }
    });

    return Array.from(uniqueSurchargeMap.values());
  }, [surchargeDetail]);


  useEffect(() => {
    if (sessionDetails) {
      generateDynamicStyling(sessionDetails);
    }
  }, [sessionDetails, activeSurchargeDetail]);
  useEffect(() => {
    if (paymentDetails && currencyCode) {
      let paymentSummaryDictionary = [
        {
          title: 'Subtotal',
          label: 'SUBTOTAL',
          value:
            !sampleToken
              ? numberToCurrency(
                showTotalPaymentSummaryField ? totalPaymentSummaryValue : (paymentDetails?.order?.originalAmount || paymentDetails?.money?.amount),
                currencyCode
              )
              : null,
        },
        {
          title: "Shipping Cost",
          label: 'SHIPPING_COST',
          type: "charges",
          value:
            !sampleToken && paymentDetails?.order?.shippingAmount != null
              ? numberToCurrency(paymentDetails?.order?.shippingAmount, currencyCode)
              : null,
        },
        // {
        //   ...!isEmpty(selectedInstrument) && !sampleToken ? {
        //     title: "Interest Charged By Bank",
        //     label: 'INTEREST_CHARGED_BY_BANK',
        //     value: numberToCurrency(selectedInstrument?.emiMethod?.bankChargedInterestAmount, currencyCode),
        //   } : {}
        // },
        {
          title: "Taxes and Fees",
          label: "TAXES_AND_FEES",
          type: "charges",
          value: numberToCurrency(paymentDetails?.order?.taxAmount, currencyCode),
        },
        {
          label: "DISCOUNT",
          title: "Discount",
          value: numberToCurrency(paymentDetails?.order?.totalDiscount, currencyCode),
          style: { color: '#2DA94F' },
          type: "discount"
        },
      ];
      let itemsPresent = paymentSummaryDictionary?.filter(
        (object) => object?.value != null
      )?.length;
      setItems(itemsPresent);
      setPaymentSummary(paymentSummaryDictionary);
    }
  }, [paymentDetails, currencyCode, selectedInstrument, appliedOfferDetail]);
  const totalPaymentErrors = (number) => {
    if (!number) {
      dispatch(handleTotalPaymentSummaryError(t("PLEASE_ENTER_AMOUNT")));
    } else if (number.startsWith("0") && number.length > 0) {
      dispatch(handleTotalPaymentSummaryError(t("AMOUNT_GREATER_THAN_ZERO")));
    } else if (number * 1 > MAXIMUM_NUMBER_AMOUNT) {
      dispatch(handleTotalPaymentSummaryError(t("HIGH_AMOUNT_ERROR_ON_DEMAND")));

    } else {
      dispatch(handleTotalPaymentSummaryError(""));
    }
  };
  const handleTotalPaymentAmount = (e) => {
    const amount = e.target.value;
    let sanitizedAmount = getSanitizedAmount(amount);
    if (amount * 1 > MAXIMUM_NUMBER_AMOUNT) return
    e.target.value = sanitizedAmount;
    totalPaymentErrors(sanitizedAmount);
    dispatch(handleTotalPaymentSummaryValue(sanitizedAmount));
    dispatch(setOnDemandAmount(sanitizedAmount))
  };
  const blockInvalidCharNumber = (e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault();

  const handleEditAmount = () => {
    onDemandAmountEditedEvent();
    // The EMI plan, the EMI list fetched for the old amount and any applied offer
    // are all tied to the old amount - drop them so the new total isn't built from them.
    dispatch(removeSelectedIntruments());
    dispatch(setEmiPaymentDetails([]));
    dispatch(setAppliedOffer(null));
    dispatch(setAppliedOfferDetails());
    dispatch(setOnDemandLoaded(false))
  }
  const handleSurchargeInfoClick = () => {
    surchargeInformationEnteredEvent();
  };
  useEffect(() => {
    const handleLanguageChange = () => {
      totalPaymentErrors(totalPaymentSummaryValue)
    };
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n, totalPaymentSummaryValue]);


  const getAmount = (isDcc, isShowSurcharge) => getNetAmount({
    isDcc,
    activePaymentMethod,
    selectedInstrument, dccMoney, amount: paymentDetails?.money?.amount,
    showTotalPaymentSummaryField, totalPaymentSummaryValue, offerAmount: appliedOfferDetail?.finalAmount,
    isShowSurcharge,
    surchargeAmount: totalSurchargeAmount
  });

  return (
    <>
      {paymentDetails && (
        <>
          {sessionDetailsLoading ? (
            <Skeleton active size="large" />
          ) : (
            <>
              {!isMobileLayout && (
                <div className="deliveryAddressHeader">
                  <div className="headerDiv">
                    <FileDoneOutlined style={{ color: "black", fontSize: "20px" }} />
                    <Typography className="deeliveryHeading ">
                      <TranslateText label={"PAYMENT_SUMMARY"}>Payment Summary</TranslateText>
                    </Typography>
                  </div>
                </div>
              )}
              <div className="domestic-payment-summary-root dynamic-font-family">
                <div className="payment-summary-desc dynamic-font-family">
                  {paymentSummary?.map((object, i) => {
                    return (
                      (object?.value || object?.valueData) && (
                        <div className="payment-summary-item dynamic-font-family" key={`summary-${i}`}>
                          <Typography
                            className="payment-summary-item-title dynamic-font-family"
                            style={{ fontFamily: fontFamily }}
                          >
                            <TranslateText label={object.label}>{t(`${object?.title}`)}</TranslateText>
                          </Typography>
                          <Typography
                            className="payment-summary-item-value dynamic-font-family"
                            style={{ fontFamily: fontFamily, ...object?.style }}
                          >
                            <span className="currency-symbol">
                              {object?.type == "charges" ? '+ ' : object?.type == "discount" ? '- ' : ''}
                              {currencyCode}{" "}
                            </span>
                            {object?.value}
                          </Typography>
                        </div>
                      )
                    );
                  })}
                  {appliedOfferDetail?.discountAmount &&
                    <div className="payment-summary-item">
                      <Typography className=" payment-summary-total-title dynamic-font-family">
                        <TranslateText label={"DISCOUNT"} >Discount </TranslateText>
                        {appliedOfferDetail?.codes?.length > 0 && <>{" "}({appliedOfferDetail?.codes.join(',')}
                          <span style={{ textTransform: "lowercase" }}> <TranslateText label={"APPLIED_TEXT"}>applied</TranslateText></span>) </>}
                      </Typography>
                      <Typography className=" payment-summary-total-value dynamic-font-family" style={{ color: "#019939" }}>
                        -   <span className="currency-symbol" style={{ fontWeight: 500 }}>{currencyCode}</span> {numberToCurrency(appliedOfferDetail?.discountAmount, currencyCode)}
                      </Typography>
                    </div>
                  }



                  {totalSurchargeAmount && (appliedOfferDetail?.finalAmount) > 0 && <>
                    <Divider
                      style={{
                        marginBottom: "0",
                        background: "#F1F1F1",
                      }}
                    />
                    <div className="payment-summary-item">
                      <Typography className="payment-summary-item-title payment-summary-total-title dynamic-font-family">
                        <TranslateText label={"DISCOUNTED_AMOUNT"} >Discounted Amount </TranslateText>
                      </Typography>
                      <Typography className=" payment-summary-total-value dynamic-font-family" style={{ color: "#019939" }}>
                        <span className="currency-symbol" style={{ fontWeight: 500 }}>{currencyCode}</span>
                        {numberToCurrency(appliedOfferDetail?.finalAmount, currencyCode)}
                      </Typography>
                    </div>
                  </>}
                  {activeSurchargeDetail?.length > 0 &&
                    <>
                      {activeSurchargeDetail?.map((surchargeSingleData, index) => {
                        const { title, description } = surchargeSingleData?.surchargeDetails || {};
                        return <div className="payment-summary-item" key={`surcharge-${index}`}>
                          <div className="payment-summary-surcharge-title-wrapper">
                            <Tooltip placement="top" title={title}>
                              <Typography className="payment-summary-total-title payment-summary-surcharge-title dynamic-font-family">
                                {title}
                              </Typography>
                            </Tooltip>
                            {description && (
                              <Tooltip
                                placement="top"
                                title={<span style={{ whiteSpace: "pre-wrap" }} dangerouslySetInnerHTML={{ __html: description }} />}
                              >
                                {/* <InfoCircleOutlined className="surcharge-info-icon" /> */}
                                <img
                                  src="/assets/address/help-circle.svg"
                                  alt="help-circle"
                                  style={{ marginLeft: "4px", width: "16px", cursor: "pointer", flexShrink: 0 }}
                                  onClick={handleSurchargeInfoClick}
                                />
                              </Tooltip>
                            )}
                          </div>
                          <Typography className="payment-summary-total-value payment-summary-surcharge-value dynamic-font-family" >
                            +  <span className="currency-symbol" style={{ fontWeight: 500 }}>{currencyCode}</span> {numberToCurrency(surchargeSingleData?.calculatedSurchargeFee, currencyCode)}
                          </Typography>
                        </div>
                      })}
                    </>
                  }
                  {!isEmpty(selectedInstrument) && !sampleToken && selectedInstrument?.emiMethod?.merchantBorneInterestAmount > 0 && <div className="payment-summary-item dynamic-font-family">
                    <Typography className="payment-summary-item-title dynamic-font-family">
                      {selectedInstrument?.emiMethod?.applicableOffer?.discount?.type === 'NoCostEmi' ? "No Cost EMI Discount" : "Low Cost EMI Discount"}
                    </Typography>
                    <Typography className=" payment-summary-total-value dynamic-font-family" style={{ color: "#019939" }}>
                      -   <span className="currency-symbol" style={{ fontWeight: 500 }}>{currencyCode}</span> {numberToCurrency(selectedInstrument?.emiMethod?.merchantBorneInterestAmount, currencyCode)}
                    </Typography>
                  </div>
                  }

                </div>

                {paymentDetails?.money?.amountLocaleFull && (
                  <div
                    className="payment-summary-total"
                    style={{ marginTop: items > 0 ? "16px" : "0px" }}
                  >
                    {(items > 0 || showTotalPaymentSummaryError) && (
                      <Divider
                        style={{
                          marginBottom: items > 0 ? "12px" : "0",
                          background: "#F1F1F1",
                        }}
                      />
                    )}

                    <div className="payment-summary-item">
                      <Typography className="semiBold18Heading payment-summary-total-title dynamic-font-family">
                        <TranslateText label={"TOTAL"}>Total amount</TranslateText>
                      </Typography>

                      {showTotalPaymentSummaryField ? (
                        <div style={{ width: "50%", display: "flex", justifyContent: "flex-end", gap: "10px", alignItems: "center" }} >
                          <Typography className="semiBold18Heading payment-summary-total-value dynamic-font-family">
                            <span className="currency-symbol"> {currencyCode}  </span>
                            {numberToCurrency(getAmount(), currencyCode)}
                          </Typography>

                          <EDIT_ICON width={20} style={{ cursor: "pointer" }} onClick={handleEditAmount} />
                        </div>
                      ) : (
                        <Typography className="semiBold18Heading payment-summary-total-value dynamic-font-family">
                          {
                            isDccQuotationSelected ? (
                              <>
                                <span className="currency-symbol"> {issuerCurrencyDetails.currencyCode}  </span>
                                {numberToCurrency(getAmount(isDccQuotationSelected), issuerCurrencyDetails.currencyCode)}
                              </>
                            ) : (
                              <>
                                <span className="currency-symbol"> {currencyCode}  </span>
                                {numberToCurrency(getAmount(), currencyCode)}

                              </>
                            )
                          }
                        </Typography>
                      )}
                    </div>
                    {showTotalPaymentSummaryField && (
                      <div style={{ display: "flex" }}>
                        <div style={{ width: "30%" }}></div>
                        <div>
                          <Typography
                            className="regular12Heading dynamic-font-family"
                            style={{ color: "var(--Red-800)" }}
                          >
                            {showTotalPaymentSummaryError}
                          </Typography>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {
                  !isMobileLayout && sessionDetails?.merchantDetails?.convenienceFeeChargedToShopper && (
                    <div className="convenience-fee-msz">
                      <TranslateText label={"CONVENIENCE_FEE_MSZ"}>
                        A convenience fee may be charged while completing your payment
                      </TranslateText>
                    </div>
                  )
                }
              </div>
              {!isEmpty(uniqueCardNetworkArray) && (
                <div className="payment-powered-by-wrapper">
                  <Typography className="payment-powered-by-title dynamic-font-family">
                    <TranslateText label={"PAYMENTS_POWERED_BY"}>Payments powered by</TranslateText>
                  </Typography>
                  <div className="payment-powered-by-logos">
                    {uniqueCardNetworkArray.map((paymentLogo, index) => (
                      <img
                        key={`payment-logo-${paymentLogo.logoUrl}-${index}`}
                        src={paymentLogo.logoUrl}
                        alt={paymentLogo.title}
                        className="payment-powered-by-logo"
                      />
                    ))}
                  </div>
                </div>
              )}
              {!isEmpty(uniqueAppliedSurcharges) && (
                <div className="payment-summary-surcharge-label-wrapper">
                  {uniqueAppliedSurcharges.map((surcharge, index) => {
                    const content = (
                      <div
                        key={`surcharge-label-${surcharge.title}-${index}`}
                        style={{ cursor: surcharge.description ? "pointer" : "default" }}
                      >
                        <div className="payment-summary-surcharge-label">
                          <span>{surcharge.title}</span>
                          {surcharge.description && (
                            <img
                              src="/assets/address/help-circle.svg"
                              alt="help-circle"
                              style={{ marginLeft: "4px", width: "16px", cursor: "pointer" }}
                              onClick={handleSurchargeInfoClick}
                            />
                          )}
                        </div>
                      </div>
                    );

                    return surcharge.description ? (
                      <Tooltip
                        key={`tooltip-${index}`}
                        placement="top"
                        title={
                          <span
                            style={{ whiteSpace: "pre-wrap" }}
                            dangerouslySetInnerHTML={{ __html: surcharge.description }}
                          />
                        }
                      >
                        {content}
                      </Tooltip>
                    ) : (
                      content
                    );
                  })}
                </div>
              )}
            </>
          )}
        </>
      )
      }
    </>
  );
};

export default PaymentSummary;
